/**
 * Generates a grid-based avatar SVG from an Ethereum address
 * Creates a 5x5 grid pattern that's symmetrical and unique per address
 */

// Color palette - vibrant colors that work well on dark backgrounds
const COLORS = [
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#F59E0B', // amber
  '#10B981', // emerald
  '#06B6D4', // cyan
  '#EF4444', // red
  '#84CC16', // lime
];

// Background colors - darker variants
const BG_COLORS = [
  '#1e293b', // slate
  '#1e1b4b', // indigo dark
  '#2e1065', // purple dark
  '#1c1917', // stone dark
];

/**
 * Simple hash function to convert address to numbers
 */
function hashAddress(address: string): number[] {
  const clean = address.toLowerCase().replace('0x', '');
  const numbers: number[] = [];
  
  for (let i = 0; i < clean.length; i += 2) {
    numbers.push(parseInt(clean.slice(i, i + 2), 16));
  }
  
  return numbers;
}

/**
 * Generate a deterministic grid pattern from an address
 * Uses a 5x5 grid with horizontal symmetry (like blockies)
 */
function generateGridPattern(address: string): boolean[][] {
  const hash = hashAddress(address);
  const grid: boolean[][] = [];
  
  // Create 5 rows
  for (let y = 0; y < 5; y++) {
    const row: boolean[] = [];
    // Only generate left half + center (3 columns), mirror for right
    for (let x = 0; x < 3; x++) {
      const idx = y * 3 + x;
      const value = hash[idx % hash.length];
      row.push(value > 127); // 50% chance based on hash byte
    }
    // Mirror: add columns 1 and 0 to make it symmetrical
    row.push(row[1]);
    row.push(row[0]);
    grid.push(row);
  }
  
  return grid;
}

/**
 * Get colors for an address
 */
export function getColors(address: string): { fg: string; bg: string } {
  const hash = hashAddress(address);
  const fgIndex = hash[0] % COLORS.length;
  const bgIndex = hash[1] % BG_COLORS.length;
  
  return {
    fg: COLORS[fgIndex],
    bg: BG_COLORS[bgIndex],
  };
}

/**
 * Generate an SVG string for the grid avatar
 */
export function generateGridAvatarSvg(address: string, size: number = 64): string {
  const grid = generateGridPattern(address);
  const { fg, bg } = getColors(address);
  const cellSize = size / 5;
  const padding = cellSize * 0.1;
  const cellInner = cellSize - padding * 2;
  const borderRadius = cellInner * 0.2;
  
  let cells = '';
  
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 5; x++) {
      if (grid[y][x]) {
        const cx = x * cellSize + padding;
        const cy = y * cellSize + padding;
        cells += `<rect x="${cx}" y="${cy}" width="${cellInner}" height="${cellInner}" rx="${borderRadius}" fill="${fg}" />`;
      }
    }
  }
  
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <rect width="${size}" height="${size}" fill="${bg}" />
    ${cells}
  </svg>`;
}

/**
 * Generate a data URL for use in img src
 */
const _avatarUrlCache = new Map<string, string>();

export function generateGridAvatarUrl(address: string, size: number = 64): string {
  const key = `${address}-${size}`;
  let url = _avatarUrlCache.get(key);
  if (!url) {
    const svg = generateGridAvatarSvg(address, size);
    url = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    _avatarUrlCache.set(key, url);
  }
  return url;
}

/**
 * Get the avatar URL for a user - returns their avatar or generates a grid avatar
 */
export function getAvatarUrl(
  address: string | null | undefined,
  existingAvatar?: string | null
): string {
  if (existingAvatar) {
    return existingAvatar;
  }
  
  if (!address) {
    // Return a generic placeholder
    return generateGridAvatarUrl('0x0000000000000000000000000000000000000000');
  }
  
  return generateGridAvatarUrl(address);
}

/**
 * Decide whether an avatar URL is safe to embed verbatim in an
 * `<img src>` or whether it should be routed through the
 * `/api/avatar/[address]` proxy.
 *
 * Safe (returned as-is):
 *   - `data:` URIs (inline-generated SVG / blob)
 *   - same-origin or root-relative paths (`/uploads/…`, `/api/…`)
 *
 * Unsafe (rewritten to the proxy):
 *   - `pbs.twimg.com` / `*.twimg.com` — Twitter rotates `400x400`
 *     URLs and stale references 404 the moment a user changes their
 *     handle or deletes the account. The proxy's DB-driven priority
 *     chain catches the failure and falls back to Farcaster / ENS /
 *     generated SVG without surfacing a console error.
 *   - Any other `http(s)://…` origin we don't control (Farcaster
 *     CDNs, IPFS gateways, ENS gateways) — same robustness rationale,
 *     plus the proxy adds in-memory + CDN caching the foreign origin
 *     may not provide.
 *
 * The address is required because the proxy is keyed by wallet
 * address — `safeAvatarUrl` is a no-op when no address is available.
 */
export function safeAvatarUrl(
  address: string | null | undefined,
  rawAvatarUrl: string | null | undefined,
): string | undefined {
  if (!rawAvatarUrl) return undefined;
  if (rawAvatarUrl.startsWith('data:')) return rawAvatarUrl;
  if (rawAvatarUrl.startsWith('/')) return rawAvatarUrl;
  if (!address) return rawAvatarUrl;
  if (!/^0x[a-fA-F0-9]{40}$/i.test(address)) return rawAvatarUrl;
  return `/api/avatar/${address.toLowerCase()}`;
}
